<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto overflow-x-hidden bg-slate-900/50 backdrop-blur-sm p-4">
    <div class="relative w-full max-w-2xl bg-white dark:bg-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-slate-200 dark:border-slate-700 animate-fade-in-up">

      <div class="flex items-center justify-between px-6 py-4 border-b border-slate-100 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
        <h3 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
          <Layers class="w-5 h-5 text-brand-500" />
          {{ isEditing ? 'Editar Cuenta Contable' : 'Nueva Cuenta Contable' }}
        </h3>
        <button @click="handleClose" class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors">
          <X class="w-5 h-5" />
        </button>
      </div>

      <form @submit.prevent="handleSubmit" class="p-6">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

          <div class="space-y-1.5 md:col-span-1">
            <label class="text-sm font-semibold text-slate-700">Código de Cuenta</label>
            <input v-model="form.code" type="text" required placeholder="Ej: 110101" class="form-input-premium">
            <p class="text-[11px] text-slate-500">El nivel se detecta según la longitud del código.</p>
          </div>

          <div class="space-y-1.5 md:col-span-1">
            <label class="text-sm font-semibold text-slate-700">Nombre de la Cuenta</label>
            <input v-model="form.name" type="text" required placeholder="Ej: Caja Chica" class="form-input-premium">
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-semibold text-slate-700">Tipo (Rubro)</label>
            <select v-model="form.account_type" required class="form-input-premium bg-white">
              <option value="ASSET">Activo (1)</option>
              <option value="LIABILITY">Pasivo (2)</option>
              <option value="EQUITY">Patrimonio (3)</option>
              <option value="REVENUE">Ingreso (4)</option>
              <option value="EXPENSE">Costo/Gasto (5)</option>
            </select>
          </div>

          <div class="space-y-1.5">
            <label class="text-sm font-semibold text-slate-700">Naturaleza</label>
            <select v-model="form.nature" required class="form-input-premium bg-white">
              <option value="DEBIT">Deudora (Aumenta con Débito)</option>
              <option value="CREDIT">Acreedora (Aumenta con Crédito)</option>
            </select>
          </div>

          <div class="space-y-1.5 md:col-span-2 mt-2">
            <label class="flex items-center p-4 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
              <div class="flex-1">
                <h4 class="text-sm font-semibold text-slate-900">Cuenta de Detalle (Acepta Asientos)</h4>
                <p class="text-xs text-slate-500">Actívalo solo si es una cuenta final donde se registrarán transacciones directamente.</p>
              </div>
              <div class="relative ml-4">
                <input v-model="form.is_selectable" type="checkbox" class="sr-only peer">
                <div class="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
              </div>
            </label>
          </div>

        </div>

        <div class="mt-8 flex justify-end gap-3 pt-6 border-t border-slate-100 dark:border-slate-700">
          <button type="button" @click="handleClose" class="px-5 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors">
            Cancelar
          </button>
          <button type="submit" :disabled="isSaving" class="inline-flex items-center justify-center px-5 py-2.5 text-sm font-bold text-white bg-brand-600 hover:bg-brand-700 rounded-xl shadow-lg shadow-brand-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed">
            <Loader2 v-if="isSaving" class="w-4 h-4 mr-2 animate-spin" />
            <Save v-else class="w-4 h-4 mr-2" />
            {{ isEditing ? 'Actualizar Cuenta' : 'Guardar Cuenta' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';
import { Layers, X, Save, Loader2 } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useToast } from '@/composables/useToast';

const props = defineProps({
  show: { type: Boolean, default: false },
  accountToEdit: { type: Object, default: null }
});

const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const isSaving = ref(false);
const isEditing = computed(() => !!props.accountToEdit);

const form = ref({
  id: null,
  code: '',
  name: '',
  account_type: 'ASSET',
  nature: 'DEBIT',
  is_selectable: false,
  company_id: null
});

// Resetea o llena el formulario cuando el modal se abre
watch(() => props.show, (newVal) => {
  if (newVal) {
    const user = SecureStorage.getItem('llevo_user');
    if (props.accountToEdit) {
      form.value = {
        ...props.accountToEdit,
        is_selectable: Boolean(props.accountToEdit.is_selectable)
      };
    } else {
      form.value = {
        id: null, code: '', name: '', account_type: 'ASSET', nature: 'DEBIT', is_selectable: false, company_id: user?.company
      };
    }
  }
});

const handleClose = () => {
  emit('close');
};

const handleSubmit = async () => {
  isSaving.value = true;
  try {
    const payload = {
      ...form.value,
      is_selectable: form.value.is_selectable ? 1 : 0
    };

    if (isEditing.value) {
      await apiClient.put(`/acc_accounts/${payload.id}`, payload);
      showSuccess('Cuenta actualizada correctamente');
    } else {
      await apiClient.post('/acc_accounts', payload);
      showSuccess('Cuenta creada correctamente');
    }

    emit('saved');
    handleClose();
  } catch (error) {
    console.error(error);
    showError('Error al guardar la cuenta contable.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(10px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.animate-fade-in-up {
  animation: fadeInUp 0.3s ease-out forwards;
}
</style>